import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import { Button } from "@/components/ui/button";
import { Shield, Car, Home, Heart, Briefcase, Plane, Baby, GraduationCap, Building2, Users, ArrowRight, Info, Stethoscope, GraduationCap as Student, UserCog, Store, Wrench, Tractor, Coffee, HeartHandshake, HelpCircle } from "lucide-react";

const FASCE_ETA = [
  { min: 18, max: 25, label: "18-25", nome: "Giovane Adulto" },
  { min: 26, max: 35, label: "26-35", nome: "Inizio Carriera" },
  { min: 36, max: 45, label: "36-45", nome: "Famiglia e Crescita" },
  { min: 46, max: 55, label: "46-55", nome: "Consolidamento" },
  { min: 56, max: 65, label: "56-65", nome: "Pre-Pensione" },
  { min: 66, max: 80, label: "66-80", nome: "Pensione e Tutela" },
];

const FASCE_REDDITO = [
  { min: 0, max: 15000, label: "Fino a 15.000", livello: "base" },
  { min: 15001, max: 28000, label: "15.000 - 28.000", livello: "medio_basso" },
  { min: 28001, max: 50000, label: "28.000 - 50.000", livello: "medio" },
  { min: 50001, max: 75000, label: "50.000 - 75.000", livello: "medio_alto" },
  { min: 75001, max: 200000, label: "Oltre 75.000", livello: "alto" },
];

const POLIZZE = [
  {
    id: "rca", nome: "RC Auto", icon: Car, link: "/preventivo-auto",
    etaMin: 18, etaMax: 80,
    desc: "Obbligatoria per legge. Protegge da danni causati a terzi.",
    livelli: {
      base: { franchigia: "500", scoperto: "10%", nota: "Franchigia alta per contenere il premio" },
      medio_basso: { franchigia: "300", scoperto: "5%", nota: "Buon compromesso qualita/prezzo" },
      medio: { franchigia: "150", scoperto: "0%", nota: "Copertura completa senza scoperto" },
      medio_alto: { franchigia: "0", scoperto: "0%", nota: "Zero franchigia, massima protezione" },
      alto: { franchigia: "0", scoperto: "0%", nota: "Kasko completa + cristalli + eventi" },
    }
  },
  {
    id: "casa", nome: "Casa e Famiglia", icon: Home, link: "/questionario/casa",
    etaMin: 26, etaMax: 80,
    desc: "Proteggi la tua abitazione e il contenuto da furti, incendi ed eventi atmosferici.",
    livelli: {
      base: { franchigia: "1.000", scoperto: "15%", nota: "Copertura essenziale incendio e furto" },
      medio_basso: { franchigia: "500", scoperto: "10%", nota: "Aggiunta eventi atmosferici" },
      medio: { franchigia: "250", scoperto: "5%", nota: "Copertura estesa con RC famiglia" },
      medio_alto: { franchigia: "150", scoperto: "0%", nota: "Copertura premium con terremoto" },
      alto: { franchigia: "0", scoperto: "0%", nota: "All-risk con oggetti di valore" },
    }
  },
  {
    id: "vita", nome: "Polizza Vita", icon: Heart, link: "/questionario/vita",
    etaMin: 25, etaMax: 65,
    desc: "Proteggi il futuro della tua famiglia con un capitale assicurato.",
    livelli: {
      base: { franchigia: "-", scoperto: "-", nota: "Caso morte base, capitale contenuto" },
      medio_basso: { franchigia: "-", scoperto: "-", nota: "Caso morte + invalidita permanente" },
      medio: { franchigia: "-", scoperto: "-", nota: "TCM + copertura mutuo + infortuni" },
      medio_alto: { franchigia: "-", scoperto: "-", nota: "Capitale elevato + LTC (non autosufficienza)" },
      alto: { franchigia: "-", scoperto: "-", nota: "TCM premium + LTC + gravi malattie" },
    }
  },
  {
    id: "infortuni", nome: "Infortuni e Malattia", icon: Stethoscope, link: "/questionario/vita",
    etaMin: 18, etaMax: 70,
    desc: "Protezione del reddito in caso di infortunio o malattia.",
    livelli: {
      base: { franchigia: "7gg", scoperto: "20%", nota: "Diaria ridotta, solo infortuni gravi" },
      medio_basso: { franchigia: "5gg", scoperto: "15%", nota: "Diaria + rimborso spese mediche base" },
      medio: { franchigia: "3gg", scoperto: "10%", nota: "Diaria + spese mediche + interventi" },
      medio_alto: { franchigia: "0gg", scoperto: "5%", nota: "Copertura completa + convalescenza" },
      alto: { franchigia: "0gg", scoperto: "0%", nota: "Premium: qualsiasi intervento + seconda opinione" },
    }
  },
  {
    id: "professione", nome: "RC Professionale", icon: Briefcase, link: "/professioni",
    etaMin: 25, etaMax: 70,
    desc: "Obbligatoria per molte professioni. Protegge da errori professionali.",
    livelli: {
      base: { franchigia: "2.500", scoperto: "10%", nota: "Massimale minimo di legge" },
      medio_basso: { franchigia: "1.500", scoperto: "10%", nota: "Massimale adeguato alla professione" },
      medio: { franchigia: "1.000", scoperto: "5%", nota: "Massimale elevato + tutela legale" },
      medio_alto: { franchigia: "500", scoperto: "0%", nota: "Massimale alto + retroattivita 10 anni" },
      alto: { franchigia: "0", scoperto: "0%", nota: "Massimale illimitato + retroattivita illimitata" },
    }
  },
  {
    id: "viaggio", nome: "Viaggio", icon: Plane, link: "/contatti?prodotto=viaggio",
    etaMin: 18, etaMax: 80,
    desc: "Annullamento, bagaglio, spese mediche all'estero.",
    livelli: {
      base: { franchigia: "200", scoperto: "20%", nota: "Solo annullamento viaggio" },
      medio_basso: { franchigia: "150", scoperto: "15%", nota: "Annullamento + spese mediche base" },
      medio: { franchigia: "75", scoperto: "10%", nota: "Completa: annullamento + mediche + bagaglio" },
      medio_alto: { franchigia: "0", scoperto: "0%", nota: "Premium + rimpatrio + assistenza 24/7" },
      alto: { franchigia: "0", scoperto: "0%", nota: "World cover: business + leisure illimitato" },
    }
  },
  {
    id: "tutela_legale", nome: "Tutela Legale", icon: Shield, link: "/questionario/aziende?polizza=tutela_legale",
    etaMin: 30, etaMax: 75,
    desc: "Copertura spese legali per controversie civili, penali, tributarie.",
    livelli: {
      base: { franchigia: "500", scoperto: "15%", nota: "Solo controversie contrattuali" },
      medio_basso: { franchigia: "300", scoperto: "10%", nota: "Civile + penale base" },
      medio: { franchigia: "150", scoperto: "5%", nota: "Civile + penale + tributario" },
      medio_alto: { franchigia: "0", scoperto: "0%", nota: "Estesa a controversie lavoro + famiglia" },
      alto: { franchigia: "0", scoperto: "0%", nota: "Omnicomprensiva + seconda opinione legale" },
    }
  },
  {
    id: "figli", nome: "Tutela Figli", icon: Baby, link: "/questionario/vita",
    etaMin: 28, etaMax: 55,
    desc: "Piano di risparmio e protezione per il futuro dei tuoi figli.",
    livelli: {
      base: { franchigia: "-", scoperto: "-", nota: "Piano risparmio base per studi" },
      medio_basso: { franchigia: "-", scoperto: "-", nota: "Risparmio + infortuni scolastici" },
      medio: { franchigia: "-", scoperto: "-", nota: "Risparmio + infortuni + malattia" },
      medio_alto: { franchigia: "-", scoperto: "-", nota: "Fondo universita + protezione completa" },
      alto: { franchigia: "-", scoperto: "-", nota: "Piano premium: universita + master + avviamento" },
    }
  },
  {
    id: "pensione", nome: "Pensione Integrativa", icon: GraduationCap, link: "/questionario/vita",
    etaMin: 25, etaMax: 60,
    desc: "Costruisci la tua pensione complementare con vantaggi fiscali.",
    livelli: {
      base: { franchigia: "-", scoperto: "-", nota: "Contributo minimo deducibile" },
      medio_basso: { franchigia: "-", scoperto: "-", nota: "Contributo medio con linea prudente" },
      medio: { franchigia: "-", scoperto: "-", nota: "Contributo ottimale con linea bilanciata" },
      medio_alto: { franchigia: "-", scoperto: "-", nota: "Massima deduzione + linea dinamica" },
      alto: { franchigia: "-", scoperto: "-", nota: "Multi-comparto + contributo massimo + TFR" },
    }
  },
  {
    id: "azienda", nome: "Polizza Azienda", icon: Building2, link: "/questionario/aziende",
    etaMin: 25, etaMax: 70,
    desc: "RC, D&O, Cyber Risk, incendio, furto per la tua attivita.",
    livelli: {
      base: { franchigia: "5.000", scoperto: "15%", nota: "Solo RC verso terzi obbligatoria" },
      medio_basso: { franchigia: "2.500", scoperto: "10%", nota: "RC + incendio locali" },
      medio: { franchigia: "1.000", scoperto: "5%", nota: "RC + incendio + furto + cyber base" },
      medio_alto: { franchigia: "500", scoperto: "0%", nota: "Estesa: D&O + cyber + tutela legale" },
      alto: { franchigia: "0", scoperto: "0%", nota: "All-risk aziendale + key man + business interruption" },
    }
  },
];

function fmt(val) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(val);
}

const PRIORITA_ETA = {
  "18-25": ["rca", "infortuni", "viaggio"],
  "26-35": ["rca", "casa", "vita", "infortuni", "professione", "viaggio"],
  "36-45": ["rca", "casa", "vita", "infortuni", "figli", "professione", "pensione", "azienda"],
  "46-55": ["rca", "casa", "vita", "infortuni", "figli", "professione", "pensione", "tutela_legale", "azienda"],
  "56-65": ["rca", "casa", "vita", "infortuni", "pensione", "tutela_legale"],
  "66-80": ["rca", "casa", "infortuni", "tutela_legale", "viaggio"],
};

// Profili attività: ogni attività porta in primo piano polizze rilevanti
// (boost = polizze prioritarie per quel profilo, indipendentemente dall'età)
const ATTIVITA = [
  { id: "non_specificato", label: "Non specifico", icon: HelpCircle, boost: [], hint: "Mostriamo le polizze tipiche per la tua età" },
  { id: "studente", label: "Studente", icon: Student, boost: ["infortuni", "viaggio", "tutela_legale"], hint: "Studi: infortuni, viaggio, tutela legale" },
  { id: "dipendente", label: "Dipendente", icon: UserCog, boost: ["vita", "infortuni", "pensione", "casa"], hint: "Reddito da tutelare + previdenza complementare" },
  { id: "libero_professionista", label: "Libero professionista", icon: Briefcase, boost: ["professione", "tutela_legale", "infortuni", "pensione"], hint: "RC professionale obbligatoria + tutela patrimoniale" },
  { id: "medico_sanitario", label: "Medico / Sanitario", icon: Stethoscope, boost: ["professione", "tutela_legale", "infortuni", "vita"], hint: "RC medica colpa grave + retroattività" },
  { id: "imprenditore", label: "Imprenditore", icon: Building2, boost: ["azienda", "tutela_legale", "vita", "pensione"], hint: "D&O, cyber, key man, RC operativa" },
  { id: "commerciante", label: "Commerciante / Artigiano", icon: Store, boost: ["azienda", "tutela_legale", "infortuni", "casa"], hint: "RC esercizio, furto, incendio locali" },
  { id: "agricoltore", label: "Agricoltore", icon: Tractor, boost: ["azienda", "infortuni", "casa", "tutela_legale"], hint: "Mezzi agricoli, fabbricati, infortuni lavoro" },
  { id: "pensionato", label: "Pensionato", icon: Coffee, boost: ["casa", "infortuni", "tutela_legale", "viaggio"], hint: "Casa, salute, viaggi senza pensieri" },
  { id: "casalingo_caregiver", label: "Casalingo / Caregiver", icon: HeartHandshake, boost: ["casa", "infortuni", "vita", "figli"], hint: "Tutela famiglia + INAIL casalinghe" },
];

export default function FunnelPage() {
  const navigate = useNavigate();
  const [eta, setEta] = useState(35);
  const [reddito, setReddito] = useState(35000);
  const [attivita, setAttivita] = useState("non_specificato");

  const fasciaEta = FASCE_ETA.find(f => eta >= f.min && eta <= f.max) || FASCE_ETA[2];
  const fasciaReddito = FASCE_REDDITO.find(f => reddito >= f.min && reddito <= f.max) || FASCE_REDDITO[2];
  const livello = fasciaReddito.livello;
  const profiloAttivita = ATTIVITA.find(a => a.id === attivita) || ATTIVITA[0];

  const polizzeConsigliate = useMemo(() => {
    const prioritaEta = PRIORITA_ETA[fasciaEta.label] || [];
    // Combina: prima le polizze "boost" dell'attività (in ordine), poi quelle per età, dedup
    const merged = [...profiloAttivita.boost, ...prioritaEta];
    const seen = new Set();
    const unique = [];
    for (const id of merged) {
      if (seen.has(id)) continue;
      seen.add(id);
      unique.push(id);
    }
    return unique
      .map(id => POLIZZE.find(p => p.id === id))
      .filter(p => p && eta >= p.etaMin && eta <= p.etaMax);
  }, [eta, fasciaEta.label, profiloAttivita.boost]);

  const essenziali = polizzeConsigliate.slice(0, 3);
  const consigliate = polizzeConsigliate.slice(3);

  const etaPercent = ((eta - 18) / (80 - 18)) * 100;
  const redditoPercent = ((reddito - 0) / (200000 - 0)) * 100;

  const LIVELLO_LABELS = {
    base: "Essenziale", medio_basso: "Base", medio: "Standard",
    medio_alto: "Premium", alto: "Top"
  };
  const LIVELLO_COLORS = {
    base: "text-amber-600 bg-amber-50 border-amber-200",
    medio_basso: "text-orange-600 bg-orange-50 border-orange-200",
    medio: "text-[#163321] bg-[#163321]/5 border-[#163321]/20",
    medio_alto: "text-[#163321] bg-[#163321]/10 border-[#163321]/30",
    alto: "text-[#163321] bg-[#163321]/15 border-[#163321]/40",
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="funnel-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 sm:w-7 sm:h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="funnel-title">
              Quali polizze ti servono?
            </h1>
            <p className="text-sm text-[#4A5E51] max-w-md mx-auto">
              Scopri le coperture assicurative ideali in base alla tua et&agrave; e al tuo reddito.
              Ogni profilo ha esigenze diverse.
            </p>
          </div>

          {/* Sliders */}
          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-4 sm:p-8 shadow-sm space-y-7 sm:space-y-8 mb-6 sm:mb-8" data-testid="funnel-sliders">
            {/* Eta */}
            <div className="space-y-3">
              <div className="flex items-end justify-between gap-3">
                <label className="text-sm font-medium text-[#0F1F14]">La tua et&agrave;</label>
                <div className="text-right">
                  <span className="text-base sm:text-lg font-heading font-medium text-[#163321] bg-[#163321]/5 px-2.5 sm:px-3 py-1 rounded-lg tabular-nums" data-testid="funnel-eta-value">{eta} anni</span>
                  <p className="text-[10px] text-[#D97B58] font-medium mt-1">{fasciaEta.nome}</p>
                </div>
              </div>
              <div className="relative h-7 flex items-center">
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2.5 bg-[#E5E2DB] rounded-full" />
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-2.5 bg-[#163321] rounded-full transition-all" style={{ width: `${etaPercent}%` }} />
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#163321] border-[3px] border-white shadow-md pointer-events-none transition-all" style={{ left: `${etaPercent}%` }} />
                <input type="range" min="18" max="80" value={eta}
                  onChange={e => setEta(parseInt(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  data-testid="funnel-eta-slider" aria-label="Età" />
              </div>
              <div className="relative h-4 text-[10px] text-[#4A5E51]">
                {[
                  { v: 18, mobile: true },
                  { v: 30, mobile: false },
                  { v: 40, mobile: true },
                  { v: 50, mobile: true },
                  { v: 60, mobile: true },
                  { v: 70, mobile: false },
                  { v: 80, mobile: true },
                ].map(({ v, mobile }) => {
                  const pct = ((v - 18) / (80 - 18)) * 100;
                  const isFirst = v === 18, isLast = v === 80;
                  return (
                    <span
                      key={v}
                      className={`absolute top-0 tabular-nums ${mobile ? '' : 'hidden sm:inline'} ${isFirst ? '' : isLast ? '-translate-x-full' : '-translate-x-1/2'}`}
                      style={{ left: `${pct}%` }}
                    >
                      {v}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Reddito */}
            <div className="space-y-3">
              <div className="flex items-end justify-between gap-3">
                <label className="text-sm font-medium text-[#0F1F14]">Reddito annuo</label>
                <div className="text-right">
                  <span className="text-base sm:text-lg font-heading font-medium text-[#D97B58] bg-[#D97B58]/10 px-2.5 sm:px-3 py-1 rounded-lg tabular-nums whitespace-nowrap" data-testid="funnel-reddito-value">{fmt(reddito)}</span>
                  <p className="text-[10px] text-[#4A5E51] mt-1 whitespace-nowrap">
                    Livello: <span className={`font-medium px-1.5 py-0.5 rounded text-[9px] border ${LIVELLO_COLORS[livello]}`}>{LIVELLO_LABELS[livello]}</span>
                  </p>
                </div>
              </div>
              <div className="relative h-7 flex items-center">
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2.5 bg-[#E5E2DB] rounded-full" />
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-2.5 bg-[#D97B58] rounded-full transition-all" style={{ width: `${redditoPercent}%` }} />
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#D97B58] border-[3px] border-white shadow-md pointer-events-none transition-all" style={{ left: `${redditoPercent}%` }} />
                <input type="range" min="0" max="200000" step="1000" value={reddito}
                  onChange={e => setReddito(parseInt(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  data-testid="funnel-reddito-slider" aria-label="Reddito annuo" />
              </div>
              <div className="relative h-4 text-[10px] text-[#4A5E51]">
                {[
                  { v: 0, label: "0", mobile: true },
                  { v: 25000, label: "25k", mobile: false },
                  { v: 50000, label: "50k", mobile: true },
                  { v: 75000, label: "75k", mobile: false },
                  { v: 100000, label: "100k", mobile: true },
                  { v: 150000, label: "150k", mobile: false },
                  { v: 200000, label: "200k+", mobile: true },
                ].map(({ v, label, mobile }) => {
                  const pct = (v / 200000) * 100;
                  const isFirst = v === 0, isLast = v === 200000;
                  return (
                    <span
                      key={v}
                      className={`absolute top-0 tabular-nums ${mobile ? '' : 'hidden sm:inline'} ${isFirst ? '' : isLast ? '-translate-x-full' : '-translate-x-1/2'}`}
                      style={{ left: `${pct}%` }}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Attività svolta — per consulenza tailor-made */}
          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-4 sm:p-8 shadow-sm mb-6 sm:mb-8" data-testid="funnel-attivita">
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="text-sm font-medium text-[#0F1F14]">Cosa fai nella vita?</label>
              <span className="text-[10px] text-[#D97B58] font-medium uppercase tracking-wider shrink-0">Tailor-made</span>
            </div>
            <p className="text-xs text-[#4A5E51] mb-4 sm:mb-5">
              Selezionando la tua attività vediamo polizze più rilevanti per il tuo profilo professionale.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {ATTIVITA.map(a => {
                const Icon = a.icon;
                const isActive = attivita === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAttivita(a.id)}
                    className={`flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 min-h-[70px] rounded-xl border-2 transition-all text-center ${
                      isActive
                        ? 'border-[#163321] bg-[#163321]/5 shadow-sm'
                        : 'border-[#E5E2DB] hover:border-[#163321]/40 bg-white'
                    }`}
                    data-testid={`funnel-attivita-${a.id}`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-[#163321]' : 'text-[#4A5E51]'}`} />
                    <span className={`text-[11px] font-medium leading-tight ${isActive ? 'text-[#0F1F14]' : 'text-[#4A5E51]'}`}>
                      {a.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {profiloAttivita.id !== "non_specificato" && (
              <div className="mt-4 flex items-start gap-2 p-3 bg-[#163321]/5 rounded-lg border border-[#163321]/10" data-testid="funnel-attivita-hint">
                <Info className="w-3.5 h-3.5 text-[#163321] mt-0.5 shrink-0" />
                <p className="text-[11px] text-[#163321] leading-relaxed">
                  <strong>{profiloAttivita.label}:</strong> {profiloAttivita.hint}
                </p>
              </div>
            )}
          </div>

          {/* Riepilogo profilo */}
          <div className="p-4 sm:p-5 bg-gradient-to-br from-[#163321] to-[#224A32] rounded-2xl text-white text-center mb-6 sm:mb-8" data-testid="funnel-profilo">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Il tuo profilo assicurativo</p>
            <p className="text-base sm:text-lg font-heading font-medium leading-snug">
              {fasciaEta.nome} &middot; {eta} anni
              {profiloAttivita.id !== "non_specificato" && <> &middot; <span className="text-[#D97B58]">{profiloAttivita.label}</span></>}
            </p>
            <p className="text-xs sm:text-sm text-white/70 mt-1">
              {polizzeConsigliate.length} polizze consigliate &middot; Livello {LIVELLO_LABELS[livello]}
            </p>
            {livello === "base" || livello === "medio_basso" ? (
              <p className="text-[10px] text-amber-300 mt-2">Franchigie e scoperti pi&ugrave; alti per ottimizzare il premio</p>
            ) : livello === "alto" || livello === "medio_alto" ? (
              <p className="text-[10px] text-green-300 mt-2">Coperture premium con franchigie minime o zero</p>
            ) : (
              <p className="text-[10px] text-white/50 mt-2">Buon equilibrio tra copertura e costo del premio</p>
            )}
          </div>

          {/* Polizze Essenziali */}
          {essenziali.length > 0 && (
            <div className="mb-8">
              <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-1">Essenziali per te</h2>
              <p className="text-xs text-[#4A5E51] mb-4">Le coperture prioritarie per la tua fascia di et&agrave;</p>
              <div className="space-y-3">
                {essenziali.map(p => (
                  <PolizzaCard key={p.id} polizza={p} livello={livello} livelloLabel={LIVELLO_LABELS[livello]} livelloColor={LIVELLO_COLORS[livello]} isPrimary />
                ))}
              </div>
            </div>
          )}

          {/* Polizze Consigliate */}
          {consigliate.length > 0 && (
            <div className="mb-8">
              <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-1">Consigliate</h2>
              <p className="text-xs text-[#4A5E51] mb-4">Coperture aggiuntive per una protezione completa</p>
              <div className="space-y-3">
                {consigliate.map(p => (
                  <PolizzaCard key={p.id} polizza={p} livello={livello} livelloLabel={LIVELLO_LABELS[livello]} livelloColor={LIVELLO_COLORS[livello]} />
                ))}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-[#F5F4F0] rounded-xl mb-6">
            <Info className="w-4 h-4 text-[#4A5E51] mt-0.5 shrink-0" />
            <p className="text-[10px] text-[#4A5E51] leading-relaxed">
              Questo &egrave; uno strumento orientativo. Franchigie, scoperti e coperture effettive dipendono dal prodotto specifico e dalla compagnia.
              Un nostro consulente ti aiuter&agrave; a trovare la soluzione perfetta per le tue esigenze.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              onClick={() => {
                const params = new URLSearchParams({
                  eta: String(eta),
                  reddito: String(reddito),
                  attivita: profiloAttivita.id,
                  livello,
                });
                navigate(`/consulenza?${params.toString()}`);
              }}
              className="w-full sm:w-auto rounded-xl h-12 px-6 sm:px-8 bg-[#163321] text-white hover:bg-[#224A32] text-sm font-medium"
              data-testid="funnel-cta"
            >
              Parla con un consulente <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-[10px] text-[#4A5E51] mt-3">
              Il nostro consulente ti contatta entro 1 ora con una proposta su misura per il tuo profilo
              {profiloAttivita.id !== "non_specificato" && <> di <strong>{profiloAttivita.label.toLowerCase()}</strong></>}.
            </p>
          </div>
          <PrivacyLinks className="mt-8" />
        </div>
      </main>
      <Footer />
      <style>{`
        input[type="range"]:focus-visible + * { outline: 2px solid #163321; outline-offset: 2px; }
      `}</style>
    </>
  );
}

function PolizzaCard({ polizza, livello, livelloLabel, livelloColor, isPrimary }) {
  const navigate = useNavigate();
  const detail = polizza.livelli[livello];
  const Icon = polizza.icon;

  return (
    <div className={`bg-white rounded-2xl border p-4 sm:p-5 transition-all hover:shadow-md group ${isPrimary ? 'border-[#163321]/30 shadow-sm' : 'border-[#E5E2DB]'}`}
      data-testid={`funnel-polizza-${polizza.id}`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 ${isPrimary ? 'bg-[#163321] text-white' : 'bg-[#163321]/5 text-[#163321]'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
            <h3 className="font-heading text-base font-medium text-[#0F1F14]">{polizza.nome}</h3>
            {isPrimary && <span className="text-[8px] font-bold uppercase tracking-wider text-[#D97B58] bg-[#D97B58]/10 px-1.5 py-0.5 rounded">Prioritaria</span>}
          </div>
          <p className="text-xs text-[#4A5E51] mb-3">{polizza.desc}</p>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`text-[9px] font-medium px-2 py-0.5 rounded border ${livelloColor}`}>
              {livelloLabel}
            </span>
            {detail.franchigia !== "-" && (
              <span className="text-[10px] text-[#4A5E51] bg-[#F5F4F0] px-2 py-0.5 rounded">
                Franchigia: &euro;{detail.franchigia}
              </span>
            )}
            {detail.scoperto !== "-" && (
              <span className="text-[10px] text-[#4A5E51] bg-[#F5F4F0] px-2 py-0.5 rounded">
                Scoperto: {detail.scoperto}
              </span>
            )}
          </div>
          <p className="text-[10px] text-[#163321] font-medium">{detail.nota}</p>
        </div>
        <button onClick={() => navigate(polizza.link)}
          className="shrink-0 w-9 h-9 rounded-full bg-[#163321]/5 hover:bg-[#163321] hover:text-white text-[#163321] flex items-center justify-center transition-all"
          data-testid={`funnel-go-${polizza.id}`}>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
